'use client';
import { Button } from '@ahafood/ui/components/button';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  getCampaignBannersFn,
  type CampaignBanner,
} from '@/api/ahafood/campaign-banner';
import { useRouter } from '@/i18n/routing';

type Props = {
  campaignId?: string;
  city?: string;
};

export default function CampaignSession({ campaignId, city }: Props) {
  const router = useRouter();
  const [banners, setBanners] = useState<CampaignBanner[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const result = await getCampaignBannersFn({
          city_id: city ?? '',
          screen: 'home',
        });
        setBanners(result);
      } catch (error) {
        setBanners(null);
      } finally {
        setLoading(false);
      }
    };
    void fetchBanners();
  }, [city]);

  if (loading) {
    return (
      <div className="mb-6 animate-pulse">
        <div className="h-48 w-full rounded-2xl bg-neutral-20 md:h-72" />
        <div className="mt-4 h-4 w-2/3 rounded bg-neutral-20" />
      </div>
    );
  }

  const banner = banners?.find((b) => b._id === Number(campaignId));

  if (!banner) {
    return (
      <div className="mb-6 flex flex-col items-center justify-center rounded-2xl border border-neutral-20 bg-white/70 py-16 text-center">
        <h1 className="text-xl font-bold md:text-2xl">
          Chiến dịch không tồn tại
        </h1>
        <p className="mt-2 text-sm text-neutral-60">Vui lòng thử lại sau.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => router.back()}
        >
          <ArrowLeft className="mr-1 size-4" /> Quay lại
        </Button>
      </div>
    );
  }

  // Ưu tiên ảnh nền (background_image_url) cho hero; fallback ảnh chính rồi ảnh
  // mặc định. Dùng .find để bỏ qua cả chuỗi rỗng, không chỉ null/undefined.
  const heroSrc =
    [banner.background_image_url, banner.image_url].find((u) => u?.trim()) ??
    '/static/images/empty-store.webp';

  return (
    <div className="mb-6">
      <div className="relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={heroSrc}
          alt={banner.title_vi_vn}
          className="h-48 w-full object-cover md:h-72"
        />
        {/* Overlay giúp chữ nổi trên ảnh */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <button
          type="button"
          onClick={() => router.back()}
          aria-label="Quay lại"
          className="absolute left-4 top-4 flex size-9 items-center justify-center rounded-full bg-white/90 text-neutral-80 shadow-sm backdrop-blur transition hover:bg-white"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="absolute inset-x-0 bottom-0 line-clamp-2 p-4 text-2xl font-bold text-white drop-shadow md:p-6 md:text-3xl">
          {banner.title_vi_vn}
        </h1>
      </div>
      {banner.description_vi_vn && (
        <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-neutral-70 md:text-base">
          {banner.description_vi_vn}
        </p>
      )}
    </div>
  );
}
